import { Button, Chip } from '@mui/material';
import { GetApp } from '@mui/icons-material';
import Timeline from '@/components/Timeline/Timeline';
import { useGetFacilityUserHistory } from '@/api/hooks/users/useGetFacilityUserHistory';
import { API_BASE_URL } from '@/config';
import {
  getUserTypeLabel,
  getActionTitle,
  getActionDescription,
  getShortChanges,
  getTimelineIconAndColor,
} from './userHistory.utils';

const UserHistorySection = ({ userId }) => {
  const { history, loading, error } = useGetFacilityUserHistory(userId);

  const getIconConfig = (item) => {
    const { Icon, color } = getTimelineIconAndColor(item.action);
    return { component: <Icon />, color };
  };

  const timelineConfig = {
    getId: (item) => item.id,
    getDate: (item) => item.changed_at,
    getIcon: getIconConfig,
    getTitle: (item) => getActionTitle(item.action),
    getDescription: getActionDescription,
    getMetadata: (item) => {
      const userTypeLabel = getUserTypeLabel(item.changed_by_user_type);
      let changedByName = null;

      if (item.changed_by?.name) {
        changedByName = item.changed_by.name;
      } else if (item.changed_by?.email) {
        changedByName = item.changed_by.email;
      } else if (item.changed_by?.id) {
        changedByName = `User #${item.changed_by.id}`;
      }

      let primary;
      if (changedByName && userTypeLabel) {
        primary = `Changed by: ${changedByName} (${userTypeLabel})`;
      } else if (changedByName) {
        primary = `Changed by: ${changedByName}`;
      } else if (userTypeLabel) {
        primary = `Changed by: ${userTypeLabel}`;
      } else {
        primary = 'Changed by: Unknown';
      }

      return {
        primary,
        secondary: getShortChanges(item),
      };
    },
    renderActions: (item) => {
      if (!item.aws_key) return null;

      const downloadUrl = `${API_BASE_URL}/getFile?filePath=${encodeURIComponent(
        item.aws_key,
      )}`;

      return (
        <Button
          size='small'
          startIcon={<GetApp />}
          variant='outlined'
          component='a'
          href={downloadUrl}
          download={item.file_name || 'download'}>
          {item.file_name || 'Download'}
        </Button>
      );
    },
    renderExtra: (item, context) =>
      (item.previous_value || item.new_value || item.metadata) && (
        <Chip
          size='small'
          label='View details'
          variant='outlined'
          color='default'
          onClick={() => context?.openModal?.(item)}
        />
      ),
    dateFormat: 'short',
    empty: {
      message: !userId
        ? 'User ID is required to view history'
        : 'No history entries found for this user',
    },
    loading: {
      skeletonCount: 3,
      skeletonHeight: 80,
    },
    detailsModal: {
      enabled: true,
      config: {
        getTitle: (item) => getActionTitle(item.action),
        getIcon: getIconConfig,
        showValueDiff: true,
        valueDiffMode: 'inline',
      },
    },
  };

  return (
    <Timeline
      items={history}
      config={timelineConfig}
      loading={loading}
      error={error}
      title='User History'
      subtitle='Timeline of all changes made to this user account'
    />
  );
};

export default UserHistorySection;
